import React, { useState, useCallback } from 'react';
import { incomingInvoicesAPI } from '@/lib/api';
import { toast } from 'sonner';

function formatCurrency(n) {
  return `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

const TERMINAL = new Set(['acknowledged', 'cancelled', 'rejected']);

// Shown when supplier has revised items since the invoice was originally sent
function RevisionDiff({ currentItems, revisions }) {
  if (!revisions?.length) return null;
  const prev = revisions[revisions.length - 1];
  const prevItems = prev.items || [];

  const changes = [];
  currentItems.forEach((ci, i) => {
    const pi = prevItems[i];
    if (!pi) { changes.push({ name: ci.product_name, type: 'added', newQty: ci.qty }); return; }
    if (Math.abs(parseFloat(ci.qty) - parseFloat(pi.qty)) > 0.001) {
      changes.push({ name: ci.product_name, type: 'changed', oldQty: pi.qty, newQty: ci.qty });
    }
  });
  prevItems.forEach((pi, i) => {
    if (!currentItems[i]) changes.push({ name: pi.product_name, type: 'removed', oldQty: pi.qty });
  });

  if (!changes.length) return null;

  return (
    <div className="mx-4 mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
      <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">
        Supplier updated quantities — changes:
      </p>
      <div className="space-y-1">
        {changes.map((c, i) => (
          <div key={i} className="text-xs text-blue-600 dark:text-blue-300 flex items-center gap-1.5">
            {c.type === 'added'   && <span className="text-green-600 dark:text-green-400 font-mono">+</span>}
            {c.type === 'removed' && <span className="text-red-500 dark:text-red-400 font-mono">−</span>}
            {c.type === 'changed' && <span className="text-amber-600 dark:text-amber-400 font-mono">~</span>}
            <span className="font-medium">{c.name}</span>
            {c.type === 'changed' && <span className="text-gray-400">{c.oldQty} → {c.newQty}</span>}
            {c.type === 'added'   && <span className="text-green-600 dark:text-green-400">qty {c.newQty} added</span>}
            {c.type === 'removed' && <span className="text-red-500 dark:text-red-400">qty {c.oldQty} removed</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InvoiceReviewModal({ invoice, onClose, onDone }) {
  const items = invoice.items || [];
  const isTerminal = TERMINAL.has(invoice.status);

  const [note, setNote]           = useState('');
  const [loading, setLoading]     = useState(false);

  // acceptMrpMap: index → boolean (true = accept supplier's MRP, false = keep existing)
  // Default true for all items that have a diff
  const [acceptMrpMap, setAcceptMrpMap] = useState(() => {
    const init = {};
    items.forEach((it, i) => {
      const supplierMrp  = parseFloat(it.mrp || 0);
      const currentMrp   = parseFloat(it.current_store_mrp || 0);
      if (supplierMrp > 0 && currentMrp > 0 && Math.abs(supplierMrp - currentMrp) > 0.01) {
        init[i] = true; // default: accept the update
      }
    });
    return init;
  });

  const totalBilled = items.reduce((s, it) => s + parseFloat(it.item_total || 0), 0);
  const hasMrpDiffs = items.some((it, i) => {
    const supplierMrp = parseFloat(it.mrp || 0);
    const currentMrp  = parseFloat(it.current_store_mrp || 0);
    return supplierMrp > 0 && currentMrp > 0 && Math.abs(supplierMrp - currentMrp) > 0.01;
  });

  const handleAccept = useCallback(async () => {
    setLoading(true);
    try {
      const acknowledgedItems = items.map((it, i) => ({
        ...it,
        qty:        parseFloat(it.qty ?? 0),
        accept_mrp: acceptMrpMap[i] !== false, // default true; false only if explicitly unchecked
      }));
      const res = await incomingInvoicesAPI.acknowledge(invoice.id, {
        acknowledged_items: acknowledgedItems,
        note,
      });
      const bill = res.data?.bill_number;
      toast.success(`Purchase recorded${bill ? ` — Bill #${bill}` : ''}`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept invoice');
    } finally {
      setLoading(false);
    }
  }, [invoice.id, items, note, acceptMrpMap, onDone]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 dark:bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl sm:rounded-xl shadow-2xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Invoice from {invoice.portal_name || 'Supplier'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {invoice.pos_bill_number && `#${invoice.pos_bill_number} · `}
              {formatDate(invoice.invoice_date)} · {invoice.payment_mode}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Revision diff */}
        {invoice.revisions?.length > 0 && (
          <RevisionDiff currentItems={items} revisions={invoice.revisions} />
        )}

        {/* MRP diff notice */}
        {hasMrpDiffs && !isTerminal && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
              MRP has changed for some items — review below
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-300">
              Checked items will update your stock master MRP. Uncheck to keep your current price.
            </p>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">

          {/* Items table */}
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2 px-1 font-medium">Product</th>
                  <th className="text-right py-2 px-1 font-medium">Qty</th>
                  <th className="text-right py-2 px-1 font-medium">MRP</th>
                  <th className="text-right py-2 px-1 font-medium">Unit Price</th>
                  <th className="text-right py-2 px-1 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {items.map((it, i) => {
                  const supplierMrp = parseFloat(it.mrp || 0);
                  const currentMrp  = parseFloat(it.current_store_mrp || 0);
                  const hasMrpDiff  = supplierMrp > 0 && currentMrp > 0 && Math.abs(supplierMrp - currentMrp) > 0.01;
                  const isNewProduct = supplierMrp > 0 && !currentMrp; // not yet in store catalog

                  return (
                    <tr key={i}>
                      <td className="py-2.5 px-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100 leading-tight">{it.product_name}</p>
                        {it.barcode && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{it.barcode}</p>
                        )}
                        {isNewProduct && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">New — will be added to catalog</p>
                        )}
                      </td>
                      <td className="py-2.5 px-1 text-right text-gray-700 dark:text-gray-300 align-top pt-3">
                        {parseFloat(it.qty || 0)}
                      </td>
                      <td className="py-2.5 px-1 text-right align-top pt-3">
                        {supplierMrp > 0 ? (
                          <div>
                            <span className="text-gray-700 dark:text-gray-300">{formatCurrency(supplierMrp)}</span>
                            {hasMrpDiff && (
                              <div className="mt-1 flex items-center justify-end gap-1.5">
                                <span className="text-xs text-gray-400 line-through">{formatCurrency(currentMrp)}</span>
                                <label className="flex items-center gap-1 cursor-pointer" title="Accept MRP update">
                                  <input
                                    type="checkbox"
                                    checked={acceptMrpMap[i] !== false}
                                    onChange={e => setAcceptMrpMap(m => ({ ...m, [i]: e.target.checked }))}
                                    className="rounded border-amber-400 text-amber-500 focus:ring-amber-400 w-3.5 h-3.5"
                                  />
                                  <span className="text-xs text-amber-600 dark:text-amber-400">Update</span>
                                </label>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-1 text-right text-gray-700 dark:text-gray-300 align-top pt-3">
                        {formatCurrency(it.unit_price)}
                      </td>
                      <td className="py-2.5 px-1 text-right font-medium text-gray-900 dark:text-gray-100 align-top pt-3">
                        {formatCurrency(it.item_total || parseFloat(it.qty || 0) * parseFloat(it.unit_price || 0))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="mt-3 flex justify-end border-t border-gray-100 dark:border-gray-800 pt-3">
            <div className="text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(totalBilled)}</p>
            </div>
          </div>

          {/* Note */}
          {!isTerminal && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Note (optional)
              </label>
              <textarea
                rows={2}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Add a note for this receipt…"
                className="w-full text-sm rounded-md border border-gray-200 dark:border-gray-700
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {!isTerminal ? (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={handleAccept}
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700
                         text-white font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving…' : 'Accept & Add to Stock ✓'}
            </button>
            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">
              If quantities differ, ask the supplier to update their invoice first.
            </p>
          </div>
        ) : (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {invoice.status === 'acknowledged'
                ? `Stock updated — Purchase Bill #${invoice.purchase_bill_number || invoice.purchase_id || '—'}`
                : `Invoice is ${invoice.status}.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
